import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiPaperclip, FiSend, FiUser, FiCheck, FiX } from 'react-icons/fi';
import { useSignalement, useSignalementMessages, useSignalementNotes } from '../hooks/useSignalements';
import '../styles/SignalementDetail.css';

const SignalementDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('information');
  const [messageText, setMessageText] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [sanctionNote, setSanctionNote] = useState('');
  const [showSanctionModal, setShowSanctionModal] = useState(false);
  const [assignedAdmin, setAssignedAdmin] = useState(null);

  const { 
    signalement, 
    loading, 
    error, 
    updateStatus, 
    assignAdmin, 
    addSanction 
  } = useSignalement(id);

  const { 
    messages, 
    sendMessage, 
    loadingMessages 
  } = useSignalementMessages(id);

  const { 
    notes, 
    addNote, 
    saveNote 
  } = useSignalementNotes(id);

  useEffect(() => {
    if (signalement) {
      setInternalNote(signalement.internal_notes || '');
      setAssignedAdmin(signalement.assigned_admin);
    }
  }, [signalement]);

  const handleSendMessage = async () => {
    if (messageText.trim()) {
      await sendMessage(messageText);
      setMessageText('');
    }
  };

  const handleSaveNote = async () => {
    await saveNote(internalNote);
  };

  const handleResolve = async () => {
    await updateStatus('Résolu');
  };

  const handleClose = async () => {
    await updateStatus('Fermé');
  };

  const handleSanctionSubmit = async () => {
    await addSanction(sanctionNote);
    setShowSanctionModal(false);
    setSanctionNote('');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Résolu': return 'status-green';
      case 'Fermé': return 'status-blue';
      case 'En cours': return 'status-orange';
      case 'Non traité': return 'status-red';
      case 'Sans suite': return 'status-gray';
      default: return '';
    }
  };

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  if (error) {
    return <div className="error">Erreur: {error}</div>;
  }

  if (!signalement) {
    return <div className="error">Signalement non trouvé</div>;
  }

  return (
    <div className="signalement-detail">
      <div className="detail-header">
        <button className="back-button" onClick={() => navigate('/signalements')}>
          <FiArrowLeft />
        </button>
        <h1>Fiche détails signalement</h1>
        <div className="header-actions">
          <button 
            className="btn btn-red" 
            onClick={handleClose}
            disabled={signalement.status === 'Fermé'}
          >
            Clôturer
          </button>
          <button 
            className="btn btn-blue" 
            onClick={handleResolve}
            disabled={signalement.status === 'Résolu'}
          >
            Résoudre
          </button>
        </div>
      </div>

      <div className="detail-content">
        <div className="detail-sidebar">
          <div className="tab-navigation">
            <button 
              className={`tab-button ${activeTab === 'information' ? 'active' : ''}`}
              onClick={() => setActiveTab('information')}
            >
              Information générale et signalement
            </button>
            <button 
              className={`tab-button ${activeTab === 'messages' ? 'active' : ''}`}
              onClick={() => setActiveTab('messages')}
            >
              Historique des échanges
            </button>
            <button 
              className={`tab-button ${activeTab === 'treatment' ? 'active' : ''}`}
              onClick={() => setActiveTab('treatment')}
            >
              Traitement du signalement
            </button>
          </div>
        </div>

        <div className="detail-main">
          {activeTab === 'information' && (
            <div className="info-tab">
              <div className="signalement-info">
                <div className="info-header">
                  <h2>Signalement #{signalement.id}</h2>
                  <span className={`status-tag ${getStatusClass(signalement.status)}`}>
                    {signalement.status}
                  </span>
                </div>

                <div className="info-grid">
                  <div className="info-section">
                    <h3>Informations générales</h3>
                    <div className="info-row">
                      <span className="label">Type de signalement:</span>
                      <span className="value">{signalement.type}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Date de création:</span>
                      <span className="value">{formatDate(signalement.created_at)}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Mission liée:</span>
                      <span className="value">{signalement.mission_id}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Cible:</span>
                      <span className="value">{signalement.target}</span>
                    </div>
                  </div>

                  <div className="info-section">
                    <h3>Signalé par</h3>
                    <div className="user-info">
                      <img 
                        src={signalement.reported_by?.avatar || '/default-avatar.png'} 
                        alt="Avatar" 
                        className="user-avatar"
                      />
                      <div className="user-details">
                        <div className="user-name">{signalement.reported_by?.name}</div>
                        <div className="user-type">{signalement.reported_by?.type}</div>
                        <div className="user-email">{signalement.reported_by?.email}</div>
                      </div>
                    </div>
                  </div>

                  <div className="info-section">
                    <h3>Personne signalée</h3>
                    <div className="user-info">
                      <img 
                        src={signalement.reported_against?.avatar || '/default-avatar.png'} 
                        alt="Avatar" 
                        className="user-avatar"
                      />
                      <div className="user-details">
                        <div className="user-name">{signalement.reported_against?.name}</div>
                        <div className="user-type">{signalement.reported_against?.type}</div>
                        <div className="user-email">{signalement.reported_against?.email}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="info-section">
                  <h3>Description du signalement</h3>
                  <div className="description-text">
                    {signalement.description}
                  </div>
                </div>

                {signalement.evidence_documents && signalement.evidence_documents.length > 0 && (
                  <div className="info-section">
                    <h3>Pièces jointes</h3>
                    <div className="attachments">
                      {signalement.evidence_documents.map((doc, index) => (
                        <div key={index} className="attachment-item">
                          <FiPaperclip />
                          <a href={doc.url} target="_blank" rel="noopener noreferrer">
                            {doc.name}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'messages' && (
            <div className="messages-tab">
              <div className="messages-container">
                {loadingMessages ? (
                  <div className="loading">Chargement des messages...</div>
                ) : (
                  <div className="messages-list">
                    {messages.map((message) => (
                      <div 
                        key={message.id} 
                        className={`message ${message.type === 'admin' ? 'admin-message' : 'user-message'}`}
                      >
                        <div className="message-avatar">
                          <img 
                            src={message.sender?.avatar || '/default-avatar.png'} 
                            alt="Avatar"
                          />
                        </div>
                        <div className="message-content">
                          <div className="message-header">
                            <span className="sender-name">{message.sender?.name}</span>
                            <span className="message-time">{formatDate(message.timestamp)}</span>
                          </div>
                          <div className="message-text">{message.content}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="message-input">
                <input
                  type="text"
                  placeholder="Tapez votre message..."
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                />
                <button onClick={handleSendMessage} disabled={!messageText.trim()}>
                  <FiSend />
                </button>
              </div>
            </div>
          )}

          {activeTab === 'treatment' && (
            <div className="treatment-tab">
              <div className="treatment-section">
                <h3>Admin assigné</h3>
                <div className="admin-assignment">
                  {assignedAdmin ? (
                    <div className="assigned-admin">
                      <img 
                        src={assignedAdmin.avatar || '/default-avatar.png'} 
                        alt="Avatar" 
                        className="admin-avatar"
                      />
                      <div className="admin-details">
                        <div className="admin-name">{assignedAdmin.name}</div>
                        <div className="admin-role">{assignedAdmin.role}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="no-admin">
                      <FiUser />
                      <span>Aucun admin assigné</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="treatment-section">
                <h3>Sanctions appliquées</h3>
                <div className="sanctions-section">
                  {signalement.sanctions ? (
                    <div className="sanctions-content">
                      <div 
                        className="sanctions-text"
                        dangerouslySetInnerHTML={{ __html: signalement.sanctions }}
                      />
                      <button 
                        className="btn btn-outline"
                        onClick={() => setShowSanctionModal(true)}
                      >
                        Modifier les sanctions
                      </button>
                    </div>
                  ) : (
                    <button 
                      className="btn btn-blue"
                      onClick={() => setShowSanctionModal(true)}
                    >
                      Ajouter des sanctions
                    </button>
                  )}
                </div>
              </div>

              <div className="treatment-section">
                <h3>Note interne</h3>
                <div className="note-editor">
                  <textarea
                    value={internalNote}
                    onChange={(e) => setInternalNote(e.target.value)}
                    placeholder="Ajouter une note interne..."
                    rows="6"
                  />
                  <button className="btn btn-blue" onClick={handleSaveNote}>
                    Enregistrer
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showSanctionModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Sanctions appliquées</h3>
              <button 
                className="modal-close"
                onClick={() => setShowSanctionModal(false)}
              >
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="rich-editor">
                <div className="editor-toolbar">
                  <button type="button"><strong>B</strong></button>
                  <button type="button"><em>I</em></button>
                  <button type="button"><u>U</u></button>
                  <button type="button">•</button>
                  <button type="button">1.</button>
                </div>
                <textarea
                  value={sanctionNote}
                  onChange={(e) => setSanctionNote(e.target.value)}
                  placeholder="Décrire les sanctions appliquées..."
                  rows="8"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-gray"
                onClick={() => setShowSanctionModal(false)}
              >
                Annuler
              </button>
              <button 
                className="btn btn-blue"
                onClick={handleSanctionSubmit}
              >
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignalementDetail;